import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdVideoLibrary, MdSave, MdCancel, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import tutorialVideoService from '../services/tutorialVideoService';

const getYoutubeVideoId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const emptyForm = { title: '', description: '', youtubeUrl: '', order: 0 };

const AdminTutorialVideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await tutorialVideoService.getAllVideos();
      if (res.success) setVideos(res.data);
    } catch {
      toast.error('Failed to load tutorial videos');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingVideo(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (video) => {
    setEditingVideo(video);
    setForm({
      title: video.title,
      description: video.description || '',
      youtubeUrl: video.youtubeUrl,
      order: video.order ?? 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.youtubeUrl.trim()) return toast.error('YouTube URL is required');
    if (!getYoutubeVideoId(form.youtubeUrl)) return toast.error('Invalid YouTube URL');

    setSaving(true);
    try {
      if (editingVideo) {
        const res = await tutorialVideoService.updateVideo(editingVideo._id, form);
        if (res.success) {
          setVideos(prev => prev.map(v => v._id === editingVideo._id ? res.data : v));
          toast.success('Video updated');
        }
      } else {
        const res = await tutorialVideoService.createVideo(form);
        if (res.success) {
          setVideos(prev => [...prev, res.data]);
          toast.success('Video added');
        }
      }
      setShowModal(false);
    } catch {
      toast.error('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (video) => {
    try {
      const res = await tutorialVideoService.updateVideo(video._id, { isActive: !video.isActive });
      if (res.success) setVideos(prev => prev.map(v => v._id === video._id ? res.data : v));
    } catch {
      toast.error('Failed to update video');
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Delete "${video.title}"?`)) return;
    try {
      const res = await tutorialVideoService.deleteVideo(video._id);
      if (res.success) {
        setVideos(prev => prev.filter(v => v._id !== video._id));
        toast.success('Video deleted');
      }
    } catch {
      toast.error('Failed to delete video');
    }
  };

  const thumbnailUrl = (url) => {
    const id = getYoutubeVideoId(url);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <MdVideoLibrary size={28} className="text-[#6B7F5C]" />
            <div>
              <h1 className="text-2xl font-bold text-[#2E2E2E]">Tutorial Videos</h1>
              <p className="text-sm text-[#7A7A7A]">Manage tutorial videos shown on the landing page</p>
            </div>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#6B7F5C] text-white rounded-lg hover:bg-[#5d7150] transition-colors"
          >
            <MdAdd size={20} />
            Add Video
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#7A7A7A]">Loading...</div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16 text-[#7A7A7A] border-2 border-dashed border-gray-200 rounded-2xl">
            <MdVideoLibrary size={48} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No tutorial videos yet</p>
            <p className="text-sm mt-1">Add YouTube video links to display on the landing page</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video) => (
              <div key={video._id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${!video.isActive ? 'opacity-60' : ''}`}>
                {thumbnailUrl(video.youtubeUrl) && (
                  <div className="relative">
                    <img
                      src={thumbnailUrl(video.youtubeUrl)}
                      alt={video.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    {!video.isActive && (
                      <div className="absolute top-2 right-2 bg-gray-700/80 text-white text-xs px-2 py-0.5 rounded-full">
                        Hidden
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-[#2E2E2E] text-sm leading-tight">{video.title}</h3>
                      {video.description && (
                        <p className="text-xs text-[#7A7A7A] mt-1 line-clamp-2">{video.description}</p>
                      )}
                      <p className="text-xs text-[#9A9A9A] mt-1">Order: {video.order}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleToggleActive(video)}
                      title={video.isActive ? 'Hide' : 'Show'}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-[#5A5A5A] hover:bg-gray-50 transition-colors"
                    >
                      {video.isActive ? <MdVisibilityOff size={14} /> : <MdVisibility size={14} />}
                      {video.isActive ? 'Hide' : 'Show'}
                    </button>
                    <button
                      onClick={() => openEditModal(video)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 border border-[#6B7F5C]/30 rounded-lg text-xs text-[#6B7F5C] hover:bg-[#6B7F5C]/5 transition-colors"
                    >
                      <MdEdit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(video)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 border border-red-200 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <MdDelete size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-[#2E2E2E] mb-5">
              {editingVideo ? 'Edit Video' : 'Add Tutorial Video'}
            </h2>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-[#5A5A5A] mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6B7F5C]"
                  placeholder="e.g. How to Apply"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5A5A5A] mb-1">YouTube URL *</label>
                <input
                  type="text"
                  value={form.youtubeUrl}
                  onChange={e => setForm(f => ({ ...f, youtubeUrl: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6B7F5C]"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                {form.youtubeUrl && !getYoutubeVideoId(form.youtubeUrl) && (
                  <p className="text-xs text-red-500 mt-1">Invalid YouTube URL</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5A5A5A] mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6B7F5C] resize-none"
                  placeholder="Short description..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#5A5A5A] mb-1">Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#6B7F5C]"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 flex items-center justify-center gap-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-[#5A5A5A] hover:bg-gray-50"
              >
                <MdCancel size={16} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1 px-4 py-2 bg-[#6B7F5C] text-white rounded-lg text-sm hover:bg-[#5d7150] disabled:opacity-60"
              >
                <MdSave size={16} /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AdminTutorialVideosPage;
